import { lazy, Suspense } from "react";
import { createBrowserRouter, RouterProvider } from "react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "sonner";
import { ApiError } from "@/api/client";
import { AuthProvider } from "@/auth/AuthProvider";
import { RequireAuth } from "@/auth/guards";
import { AppShell } from "@/layout/AppShell";
import { Spinner } from "@/components/ui/States";
import { useResolvedTheme } from "@/lib/theme";
import { LoginPage } from "@/pages/LoginPage";
import { AuthCallbackPage } from "@/pages/AuthCallbackPage";
import { OverviewPage } from "@/pages/OverviewPage";
import { NotFoundPage } from "@/pages/NotFoundPage";

// Heavier, less-visited pages load on demand.
const AttacksPage = lazy(() => import("@/pages/AttacksPage").then((m) => ({ default: m.AttacksPage })));
const TrafficPage = lazy(() => import("@/pages/TrafficPage").then((m) => ({ default: m.TrafficPage })));
const RulesPage = lazy(() => import("@/pages/RulesPage").then((m) => ({ default: m.RulesPage })));
const BlackholesPage = lazy(() => import("@/pages/BlackholesPage").then((m) => ({ default: m.BlackholesPage })));
const PamPage = lazy(() => import("@/pages/PamPage").then((m) => ({ default: m.PamPage })));
const FiltersPage = lazy(() => import("@/pages/FiltersPage").then((m) => ({ default: m.FiltersPage })));
const AliasesPage = lazy(() => import("@/pages/AliasesPage").then((m) => ({ default: m.AliasesPage })));
const FeedsPage = lazy(() => import("@/pages/FeedsPage").then((m) => ({ default: m.FeedsPage })));
const NodesPage = lazy(() => import("@/pages/NodesPage").then((m) => ({ default: m.NodesPage })));
const LicensesPage = lazy(() => import("@/pages/LicensesPage").then((m) => ({ default: m.LicensesPage })));
const AccessPage = lazy(() => import("@/pages/AccessPage").then((m) => ({ default: m.AccessPage })));
const AuditPage = lazy(() => import("@/pages/AuditPage").then((m) => ({ default: m.AuditPage })));
const MetricsPage = lazy(() => import("@/pages/MetricsPage").then((m) => ({ default: m.MetricsPage })));
const SettingsPage = lazy(() => import("@/pages/SettingsPage").then((m) => ({ default: m.SettingsPage })));

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 10_000,
        refetchOnWindowFocus: true,
        // Auth/permission/validation errors won't fix themselves on retry.
        retry: (count, err) => !(err instanceof ApiError && err.status >= 400 && err.status < 500) && count < 2,
      },
    },
  });
}

const page = (el: React.ReactNode) => <Suspense fallback={<Spinner />}>{el}</Suspense>;

const router = createBrowserRouter([
  { path: "/login", element: <LoginPage /> },
  { path: "/auth/callback", element: <AuthCallbackPage /> },
  {
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <OverviewPage /> },
      { path: "attacks", element: page(<AttacksPage />) },
      { path: "traffic", element: page(<TrafficPage />) },
      { path: "rules/blacklist", element: page(<RulesPage list="blacklist" />) },
      { path: "rules/whitelist", element: page(<RulesPage list="whitelist" />) },
      { path: "blackholes", element: page(<BlackholesPage />) },
      { path: "pam", element: page(<PamPage />) },
      { path: "filters", element: page(<FiltersPage />) },
      { path: "aliases", element: page(<AliasesPage />) },
      { path: "feeds", element: page(<FeedsPage />) },
      { path: "nodes", element: page(<NodesPage />) },
      { path: "licenses", element: page(<LicensesPage />) },
      { path: "access", element: page(<AccessPage />) },
      { path: "audit", element: page(<AuditPage />) },
      { path: "prometheus", element: page(<MetricsPage />) },
      { path: "settings", element: page(<SettingsPage />) },
      { path: "*", element: <NotFoundPage /> },
    ],
  },
]);

function ThemedToaster() {
  const theme = useResolvedTheme();
  return <Toaster theme={theme} position="bottom-right" richColors closeButton />;
}

export function App({ queryClient }: { queryClient: QueryClient }) {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <RouterProvider router={router} />
        <ThemedToaster />
      </AuthProvider>
    </QueryClientProvider>
  );
}
